import { send } from '@actual-app/core/platform/client/connection';
import { currentDay } from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';
import {
  realizeTempTransactions,
  ungroupTransactions,
  updateTransaction,
} from '@actual-app/core/shared/transactions';
import { applyChanges } from '@actual-app/core/shared/util';
import type {
  AccountEntity,
  TransactionEntity,
} from '@actual-app/core/types/models';
import { t } from 'i18next';

import { aqlQuery } from '#queries/aqlQuery';

/**
 * `hasta` (Rumbo, móvil): solo movimientos de esa fecha o antes; lo apuntado
 * para días que aún no han llegado no entra en el saldo ni se bloquea.
 */
function hastaFecha(hasta?: string) {
  return hasta ? { date: { $lte: hasta } } : {};
}

export async function lockTransactions(
  accountId: AccountEntity['id'],
  hasta?: string,
) {
  const { data } = await aqlQuery(
    q('transactions')
      .filter({
        cleared: true,
        reconciled: false,
        account: accountId,
        ...hastaFecha(hasta),
      })
      .select('*')
      .options({ splits: 'grouped' }),
  );
  let transactions = ungroupTransactions(data);

  const changes: { updated: Array<Partial<TransactionEntity>> } = {
    updated: [],
  };

  transactions.forEach(trans => {
    const { diff } = updateTransaction(transactions, {
      ...trans,
      reconciled: true,
    });

    transactions = applyChanges(diff, transactions);

    changes.updated = changes.updated
      ? changes.updated.concat(diff.updated)
      : diff.updated;
  });

  await send('transactions-batch-update', changes);
}

export async function unlockTransaction(
  transactionId: TransactionEntity['id'],
) {
  const { data } = await aqlQuery(
    q('transactions')
      .filter({ id: transactionId })
      .select('*')
      .options({ splits: 'grouped' }),
  );
  const transactions = ungroupTransactions(data);

  const transaction = transactions.find(trans => trans.id === transactionId);
  if (!transaction) {
    return;
  }

  const { diff } = updateTransaction(transactions, {
    ...transaction,
    reconciled: false,
  });

  await send('transactions-batch-update', { updated: diff.updated });
}

export async function getClearedBalance(
  accountId: AccountEntity['id'],
  hasta?: string,
) {
  const { data } = await aqlQuery(
    q('transactions')
      .filter({ cleared: true, account: accountId, ...hastaFecha(hasta) })
      .options({ splits: 'none' })
      .calculate({ $sum: '$amount' }),
  );

  return data ?? 0;
}

export async function finishReconciliation(
  accountId: AccountEntity['id'],
  reconcileAmount: number | null,
  lock?: () => Promise<void>,
  hasta?: string,
) {
  const cleared = await getClearedBalance(accountId, hasta);
  const targetDiff = (reconcileAmount ?? 0) - cleared;

  if (targetDiff === 0) {
    await (lock ?? (() => lockTransactions(accountId, hasta)))();
  }
}

/**
 * Rumbo: adónde va el ajuste de «Cuadrar». No es un gasto ni un ingreso del
 * plan: es dinero que ya estaba (o que no estaba) en la cuenta. En una
 * cuenta del presupuesto va a la misma categoría de ingresos que el saldo
 * inicial, así «A asignar» sube o baja lo mismo que el saldo y el ajuste no
 * pide categoría en «Por revisar». Fuera del presupuesto, sin categoría.
 */
export async function categoriaDelAjuste(
  accountId: AccountEntity['id'],
): Promise<string | null> {
  const { data: cuentas } = await aqlQuery(
    q('accounts').filter({ id: accountId }).select(['offbudget']),
  );
  if (!cuentas?.[0] || cuentas[0].offbudget) {
    return null;
  }
  // Como el saldo inicial: «Starting Balances» o el primer ingreso.
  const { data: categorias } = await aqlQuery(
    q('categories')
      .filter({ is_income: true })
      .select(['id', 'name'])
      .orderBy({ sort_order: 'asc' }),
  );
  const ingresos = (categorias ?? []) as { id: string; name: string }[];
  const categoria =
    ingresos.find(c => c.name.toLowerCase() === 'starting balances') ??
    ingresos[0];
  return categoria?.id ?? null;
}

export async function createReconciliationTransaction(
  accountId: AccountEntity['id'],
  diff: number,
  onRealized?: (transactions: TransactionEntity[]) => void,
) {
  const categoria = await categoriaDelAjuste(accountId);
  const reconciliationTransactions = realizeTempTransactions([
    {
      id: 'temp',
      account: accountId,
      cleared: true,
      reconciled: false,
      amount: diff,
      date: currentDay(),
      notes: t('Reconciliation balance adjustment'),
      ...(categoria ? { category: categoria, reviewed: true } : null),
    },
  ]);

  onRealized?.(reconciliationTransactions);

  const ruledTransactions = await Promise.all(
    reconciliationTransactions.map(transaction =>
      send('rules-run', { transaction }),
    ),
  );

  const added = ruledTransactions.filter(trans => !trans.tombstone);
  await send('transactions-batch-update', {
    added,
    deleted: ruledTransactions.filter(trans => trans.tombstone),
  });
  // Rumbo (H26): los ids creados, para poder deshacer el ajuste.
  return added.map(trans => trans.id);
}

/**
 * Rumbo (H26): «Deshacer» del ajuste creado desde «Cuadrar». Borra solo los
 * movimientos de ajuste que se crearon; crear el ajuste no marca nada como
 * conciliado, así que la cuenta vuelve a quedar como estaba.
 */
export async function deleteReconciliationTransactions(
  ids: ReadonlyArray<TransactionEntity['id']>,
) {
  if (ids.length === 0) {
    return;
  }
  await send('transactions-batch-update', {
    deleted: ids.map(id => ({ id })),
  });
}
